import Avatar from '@components/avatar'
import PropTypes from 'prop-types'
import classnames from 'classnames'

/**
 * CustomAvatar
 *
 * Renders the user/brand profile image if available.
 * If missing or default placeholder, renders a Google-style avatar:
 * a solid dark-colored background badge displaying 1 or 2 crisp uppercase ALPHABET-ONLY initial letters (no special chars, hyphens, or punctuation).
 */
export const getAvatarProps = (name, imgUrl) => {
    const hasImage = imgUrl &&
        typeof imgUrl === 'string' &&
        imgUrl.trim() !== '' &&
        !imgUrl.includes('avatar-blank') &&
        !imgUrl.includes('Blank-logo') &&
        !imgUrl.includes('blank') &&
        !imgUrl.includes('null') &&
        !imgUrl.includes('undefined')

    if (hasImage) {
        return { img: imgUrl }
    }

    // Rich dark background colors for Google/Slack style avatars
    const colors = [
        'primary',
        'success',
        'info',
        'warning',
        'danger',
        'dark'
    ]

    const rawStr = (name || 'User').trim()

    // Strip out all non-alphabetic characters except spaces (e.g. "U.S. Soy" -> "US Soy", "Intoleran - Digestive" -> "Intoleran Digestive")
    const cleanStr = rawStr
        .replace(/[^a-zA-Z\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim()

    const strToUse = cleanStr.length > 0 ? cleanStr : 'User'

    let hash = 0
    for (let i = 0; i < rawStr.length; i++) {
        hash = rawStr.charCodeAt(i) + ((hash << 5) - hash)
    }
    const colorIndex = Math.abs(hash) % colors.length
    const color = colors[colorIndex]

    const words = strToUse.split(/\s+/)
    let initials = ''
    if (words.length >= 2) {
        initials = (words[0][0] + words[1][0]).toUpperCase()
    } else if (words.length === 1 && words[0].length >= 2) {
        initials = words[0].substring(0, 2).toUpperCase()
    } else if (words.length === 1 && words[0].length === 1) {
        initials = words[0][0].toUpperCase()
    } else {
        initials = 'U'
    }

    return {
        color,
        content: initials,
        initials: false,
        img: false,
        contentStyles: { color: '#ffffff', fontWeight: '600' }
    }
}

const CustomAvatar = props => {
    const { name, img, size, className, imgClassName, imgHeight, imgWidth, style, ...rest } = props
    const avatarProps = getAvatarProps(name, img)

    const targetSize = parseInt(imgHeight || imgWidth || 42, 10) || 42

    const combinedStyle = {
        width: `${targetSize}px`,
        height: `${targetSize}px`,
        minWidth: `${targetSize}px`,
        minHeight: `${targetSize}px`,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
    }

    return (
        <Avatar
            {...avatarProps}
            size={size}
            className={classnames('msg-avatar', className)}
            imgClassName={imgClassName}
            imgHeight={targetSize}
            imgWidth={targetSize}
            style={combinedStyle}
            {...rest}
        />
    )
}

CustomAvatar.propTypes = {
    name: PropTypes.string,
    img: PropTypes.string,
    size: PropTypes.string,
    className: PropTypes.string,
    imgClassName: PropTypes.string,
    imgHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    imgWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default CustomAvatar
